import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, MapPinned, Siren, Settings2, LogOut, Radar, Map as MapIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JobTracker } from "@/components/JobTracker";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/meldingen", label: "Meldingen", icon: Siren, id: "nav-meldingen" },
  { to: "/kaart", label: "Kaart & route", icon: MapIcon, id: "nav-kaart" },
  { to: "/adressen", label: "Adressen & percelen", icon: MapPinned, id: "nav-adressen" },
  { to: "/instellingen", label: "Basisjaar", icon: Settings2, id: "nav-instellingen" },
];

const Brand = () => (
  <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800">
    <div className="h-8 w-8 rounded-md bg-blue-600 grid place-items-center"><Radar className="h-4 w-4" /></div>
    <div>
      <div className="font-display font-bold tracking-tight leading-none">GeoInspect</div>
      <div className="font-mono text-[10px] text-slate-500 tracking-widest">BAG · BRK · LUCHTFOTO</div>
    </div>
  </div>
);

const YearBadge = () => {
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  if (!data) return null;
  return (
    <div data-testid="header-year-badge" className="font-mono text-xs border border-slate-700 rounded-md px-3 py-1.5 text-slate-300">
      BASIS <span className="text-white font-medium">{data.baseline_year}</span>
      <span className="text-slate-500 mx-2">→</span>
      NIEUW <span className="text-amber-400 font-medium">{data.compare_year}</span>
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex">
      <aside className="print:hidden hidden md:flex w-60 shrink-0 flex-col border-r border-slate-800 bg-[#0b111d] sticky top-0 h-screen">
        <Brand />
        <nav className="p-3 space-y-1 flex-1">
          {NAV.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={id}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-blue-600/15 text-white border border-blue-600/30" : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"}`}>
              <Icon className="h-4 w-4" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800 text-xs">
          <div className="text-slate-300 truncate" data-testid="current-user-email">{user?.email}</div>
          <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">{user?.role}</div>
          <button data-testid="logout-btn" onClick={logout} className="mt-3 flex items-center gap-2 text-slate-400 hover:text-rose-400 transition-colors">
            <LogOut className="h-3.5 w-3.5" /> Uitloggen
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="print:hidden h-16 sticky top-0 z-[1000] backdrop-blur-md bg-[#090D16]/85 border-b border-slate-800 flex items-center justify-between px-4 sm:px-8 gap-3">
          <div className="flex md:hidden gap-1 overflow-x-auto">
            {NAV.map(({ to, icon: Icon, id }) => (
              <NavLink key={to} to={to} end={to === "/"} data-testid={`m-${id}`} className={({ isActive }) => `p-2 rounded-md ${isActive ? "bg-blue-600/20 text-white" : "text-slate-400"}`}>
                <Icon className="h-4 w-4" />
              </NavLink>
            ))}
          </div>
          <div className="hidden md:block font-mono text-[11px] tracking-widest text-slate-500">OMGEVINGSDIENST · WIJZIGINGSDETECTIE</div>
          <div className="flex items-center gap-3"><JobTracker /><YearBadge /></div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 grid-bg">{children}</main>
      </div>
    </div>
  );
}
